import styles from './FooterAtendimento.module.css';
import HBox from '../../../components/layout/HBox';
import Modal from '../../../components/layout/Modal';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { lerRascunho, limparRascunho, salvarRascunho } from '../rascunhos';
import Botao from '../../../components/Botao';
import {
  mdiAccountGroup,
  mdiAttachment,
  mdiCircle,
  mdiInformationSlabCircle,
  mdiMessageBookmark,
  mdiMicrophone,
  mdiSendVariant,
  mdiTagPlus,
  mdiTrashCan,
} from '@mdi/js';
import { useDebounce } from '../../../hooks/useDebounce';
import ArquivoAnexo from '../ArquivoAnexo';
import Icon from '@/components/Icon';

import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record';
import BotaoDropdown from '../../../components/BotaoDropdown';
import Titulo from '../../../components/tipografia/Titulo';
import VBox from '../../../components/layout/VBox';
import Subtitulo from '../../../components/tipografia/Subtitulo';
import { useMutation, useQuery } from 'react-query';
import axios from 'axios';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import { toast } from 'react-toastify';
import AlertBox from '@/components/layout/AlertBox';
import { useCooldown } from '@/hooks/useCooldown';
import CampoUpload from '@/components/Formulario/CampoUpload';
import CampoMultiTexto from '@/components/Formulario/CampoMultiTexto';

type AtalhosBoxProps = {
  atalhosPessoais: any[];
  atalhosGlobais: any[];
  onSelectAtalho: (atalho: string) => void;
};

const AtalhosBox = ({
  atalhosGlobais,
  atalhosPessoais,
  onSelectAtalho,
}: AtalhosBoxProps) => {
  return (
    <div style={{ width: '50vw', color: 'var(--tc-color-gray-800)' }}>
      <p>
        <Icon
          path={mdiInformationSlabCircle}
          size={1}
          color="var(--tc-color-primary)"
        />{' '}
        Para editar, vá em &quot;Atalhos de Mensagens&quot; do menu principal
      </p>
      <HBox style={{ height: '100%', alignItems: 'flex-start' }}>
        <VBox>
          <Subtitulo>Atalhos pessoais</Subtitulo>
          {atalhosPessoais?.map((atalho: any, idx: number) => (
            <button
              key={idx}
              type="button"
              className={styles.atalhoContainer}
              title={atalho.conteudo}
              onClick={() => {
                onSelectAtalho(atalho.conteudo);
              }}
            >
              <span>
                {atalho.favorito ? '⭐ ' : ''}
                {atalho.atalho}
              </span>

              <HBox style={{ width: 'auto' }}>
                {atalho.grupo && (
                  <span>
                    <Icon
                      path={mdiAccountGroup}
                      size={1}
                      color="var(--tc-color-gray-500)"
                    />
                  </span>
                )}
                {idx < 9 && (
                  <span>
                    <span className={styles.atalhoBadge}>Ctrl+{idx + 1}</span>
                  </span>
                )}
              </HBox>
            </button>
          ))}
        </VBox>
        <VBox>
          <Subtitulo>Atalhos gerais</Subtitulo>
          {atalhosGlobais?.map((atalho: any, idx: number) => (
            <button
              key={idx}
              type="button"
              className={styles.atalhoContainer}
              title={atalho.conteudo}
              onClick={() => {
                onSelectAtalho(atalho.conteudo);
              }}
            >
              {atalho.atalho}{' '}
              {idx < 9 && (
                <span className={styles.atalhoBadge}>Alt+{idx + 1}</span>
              )}
            </button>
          ))}
        </VBox>
      </HBox>
    </div>
  );
};

type FooterAtendimentoProps = {
  chatId: number;
  /** `chatKey` da conversa aberta, usada para guardar o rascunho. */
  rascunhoKey: string | null;
  chatTags?: string[];
  onChatAddTag: (chatId: number, tagId: number) => void;
  onSend: (mensagem: string, anexos: File[]) => void;
  isSending: boolean;
  onSendAudio: (audio: Blob) => void;
  onTyping?: (isTyping: boolean) => void;
};

const MAX_FILE_SIZE = 25 * 1024 * 1024;

/** Espera de digitação antes de gravar o rascunho, para não escrever a cada tecla. */
const RASCUNHO_DEBOUNCE_MS = 400;

export const FooterAtendimento = ({
  chatId,
  rascunhoKey,
  chatTags,
  onChatAddTag,
  onSend,
  isSending,
  onSendAudio,
  onTyping,
}: FooterAtendimentoProps) => {
  const [mensagem, setMensagem] = React.useState<string>('');
  // Anexo vive no modal, não no compositor: entre abrir e enviar não existe
  // estado intermediário pendurado na barra de mensagem.
  const [showAttachments, setShowAttachments] = React.useState<boolean>(false);
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const [legenda, setLegenda] = React.useState<string>('');
  const [recording, setRecording] = React.useState<boolean>(false);
  const [recordProgress, setRecordProgress] = React.useState<string>('00:00');

  const TYPING_COOLDOWN = 4000; // 4 segundos de cooldown

  const [triggerIsTyping, resetTriggerIsTyping] = useCooldown(TYPING_COOLDOWN);
  const timeoutIsTyping = useRef<NodeJS.Timeout | null>(null);

  const waveformRef = React.useRef<HTMLDivElement>(null);
  const wavesurfer = React.useRef<WaveSurfer | null>(null);
  const record = React.useRef<any>(null);

  const cancelled = React.useRef<boolean>(false);

  const [showAtalhos, setShowAtalhos] = React.useState<boolean>(false);

  const queryAtalhos = useQuery(['atalho'], async () => {
    const response = await axios.get('/api/chat_mensagem_atalho/box');

    return response.data;
  });

  const qTags = useQuery(['tags'], async () => {
    const response = await axios.get('/api/marcador/box');
    return response.data?.result;
  });

  const optionsTags = qTags.data
    ?.filter(
      (it: any) =>
        ['qualquer', 'chat'].includes(it.local) && !chatTags?.includes(it.nome),
    )
    ?.map((it: any) => ({
      id: it.id,
      label: it.nome,
    }));

  const atalhosPessoais = queryAtalhos.data?.result
    ?.filter((it: any) => !it.global)
    ?.sort((a: any, b: any) => {
      const favA = a.favorito ? 1 : 0;
      const favB = b.favorito ? 1 : 0;
      if (favA !== favB) return favB - favA;
      return a.indice - b.indice;
    });
  const atalhosGlobais = queryAtalhos.data?.result
    ?.filter((it: any) => !!it.global)
    ?.sort((a: any, b: any) => a.indice - b.indice);

  React.useEffect(() => {
    if (!recording) return;

    if (waveformRef.current) {
      wavesurfer.current = WaveSurfer.create({
        container: waveformRef.current,
        dragToSeek: true,
        normalize: false,
        waveColor: '#6f8090',
        progressColor: '#2f8719',
        cursorColor: '#2f8719',
        barWidth: 2,
        barGap: 4,
        barRadius: 2,
        height: 40,
        cursorWidth: 16,
      });

      record.current = wavesurfer.current.registerPlugin(
        RecordPlugin.create({
          scrollingWaveform: false,
          renderRecordedAudio: false,
        }),
      );
      record.current.on('record-end', (blob: Blob) => {
        if (cancelled.current) {
          cancelled.current = false;
          return;
        }

        // const recordedUrl = URL.createObjectURL(blob)
        // console.log('recorded', recordedUrl)
        onSendAudio(blob);
        setRecording(false);
      });

      record.current.on('record-progress', (time: number) => {
        console.log('progress', time);
        const formattedTime = [
          Math.floor((time % 3600000) / 60000), // minutes
          Math.floor((time % 60000) / 1000), // seconds
        ]
          .map(v => (v < 10 ? '0' + v : v))
          .join(':');

        setRecordProgress(formattedTime);
      });
    }
    const wavesurferInstance = wavesurfer.current;
    return () => {
      wavesurferInstance?.destroy();
    };
  }, [recording]);

  const campoMensagemRef = React.useRef<HTMLTextAreaElement>(null);

  const rascunhoTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rascunhoPendente = useRef<{ chave: string | null; texto: string } | null>(null);

  const descartarRascunhoPendente = useCallback(() => {
    if (rascunhoTimeout.current) clearTimeout(rascunhoTimeout.current);
    rascunhoTimeout.current = undefined;
    rascunhoPendente.current = null;
  }, []);

  const gravarRascunhoPendente = useCallback(() => {
    const pendente = rascunhoPendente.current;
    descartarRascunhoPendente();
    // A chave vai junto do texto: ao trocar de conversa o que ficou pendente
    // ainda pertence à conversa anterior.
    if (pendente) salvarRascunho(pendente.chave, pendente.texto);
  }, [descartarRascunhoPendente]);

  /** Toda edição do texto passa por aqui, para o rascunho acompanhar. */
  const atualizarMensagem = (texto: string) => {
    setMensagem(texto);
    rascunhoPendente.current = { chave: rascunhoKey, texto };

    if (rascunhoTimeout.current) clearTimeout(rascunhoTimeout.current);
    rascunhoTimeout.current = setTimeout(gravarRascunhoPendente, RASCUNHO_DEBOUNCE_MS);
  };

  /** Zera o modal sem devolver nada ao compositor. */
  const limparAnexos = useCallback(() => {
    setShowAttachments(false);
    setAttachments([]);
    setLegenda('');
  }, []);

  /** Cancelar devolve a legenda ao compositor: foi de lá que ela saiu. */
  const cancelarAnexos = () => {
    atualizarMensagem(legenda);
    limparAnexos();
    campoMensagemRef.current?.focus();
  };

  const enviarAnexos = () => {
    if (!attachments.length || isSending) return;
    onSend(legenda, attachments);
  };

  // Só nas bordas de envio: antes isso rodava também na montagem, e passaria a
  // atropelar a restauração do rascunho.
  const enviandoAnterior = useRef(isSending);
  const chaveEnvio = useRef<string | null>(null);
  useEffect(() => {
    const comecouEnvio = !enviandoAnterior.current && isSending;
    const terminouEnvio = enviandoAnterior.current && !isSending;
    enviandoAnterior.current = isSending;

    // O envio pertence à conversa em que foi disparado — o atendente pode trocar
    // de conversa antes de ele terminar.
    if (comecouEnvio) {
      chaveEnvio.current = rascunhoKey;
      return;
    }
    if (!terminouEnvio) return;

    const chave = chaveEnvio.current;
    chaveEnvio.current = null;

    // A mensagem saiu: o rascunho de onde ela saiu morre junto.
    limparRascunho(chave);

    // Já está em outra conversa: mexer no compositor ou no pendente daqui
    // apagaria texto que é de outra conversa.
    if (chave !== rascunhoKey) return;

    descartarRascunhoPendente();
    setMensagem('');
    campoMensagemRef.current?.focus();
    // A legenda saiu junto com os arquivos: não volta para o compositor.
    limparAnexos();
  }, [isSending, rascunhoKey, descartarRascunhoPendente, limparAnexos]);

  // Abriu ou trocou de conversa: traz o rascunho dela. A limpeza grava o que
  // tiver ficado pendente da conversa que está saindo (inclusive ao desmontar).
  useEffect(() => {
    setMensagem(lerRascunho(rascunhoKey));
    campoMensagemRef.current?.focus();

    // Anexo não tem rascunho, então é descartado junto com a conversa. Mantê-lo
    // seria pior que perdê-lo: o arquivo continua no disco de quem anexou, mas
    // engatilhado aqui ele sairia na próxima mensagem — para o cliente errado.
    // O texto que virou legenda não se perde: o rascunho gravado continua lá.
    setShowAttachments(false);
    setAttachments([]);
    setLegenda('');

    return () => {
      gravarRascunhoPendente();
    };
  }, [rascunhoKey, gravarRascunhoPendente]);

  // Abrir o modal move o texto do compositor para a legenda: quem escreveu e
  // então anexou quis mandar as duas coisas juntas, não duas mensagens. O
  // rascunho gravado fica intocado de propósito — é a rede de segurança caso o
  // modal seja cancelado ou a conversa troque com ele aberto.
  const mensagemRef = useRef(mensagem);
  mensagemRef.current = mensagem;
  useEffect(() => {
    if (!showAttachments) return;
    setLegenda(mensagemRef.current);
    setMensagem('');
  }, [showAttachments]);

  const handlePaste = (e: React.ClipboardEvent) => {
    const items: DataTransferItemList = e.clipboardData?.items;
    if (!items) return;

    const _files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const _file = item.getAsFile();
        if (_file && _file.size > MAX_FILE_SIZE) {
          toast(`O tamanho máximo de arquivo é ${MAX_FILE_SIZE / 1024 / 1024}MB`, {
            toastId: 'error',
            position: 'top-center',
            type: 'warning',
            autoClose: 5000,
          });
          return;
        }

        _files.push(item.getAsFile()!);
      }
    }

    if (_files.length) {
      // Print colado cai no mesmo lugar que o clipe: confirma no modal e envia.
      setAttachments(p => [...p, ..._files]);
      setShowAttachments(true);
      e.preventDefault();
    }
  };

  const recAudio = () => {
    // if (record.current.isRecording() || record.current.isPaused()) {
    //     record.current.stopRecording()
    //     setRecording(false)
    //     return
    // }

    cancelled.current = false;
    setRecording(true);
    // reset the wavesurfer instance

    // get selected device
    RecordPlugin.getAvailableAudioDevices().then(devices => {
      if (devices?.length) {
        const deviceId = devices[0].deviceId;

        record.current.startRecording({ deviceId }).then(() => {
          console.log('start recording');
        });
      }

      // devices.forEach((device) => {
      //   const option = document.createElement('option')
      //   option.value = device.deviceId
      //   option.text = device.label || device.deviceId
      //   micSelect.appendChild(option)
      // })
    });
  };

  const cancelRecording = () => {
    cancelled.current = true;
    record.current.stopRecording();
    setRecording(false);
  };

  const sendAudio = () => {
    record.current.stopRecording();
  };

  const dragTimerRef = useRef(null);
  const dragOverEventListener = (e: DragEvent) => {
    const dt = e.dataTransfer;
    if (
      dt?.types &&
      (dt.types.indexOf
        ? dt.types.indexOf('Files') !== -1
        : dt.types.includes('Files'))
    ) {
      console.log('dragover');
      setShowAttachments(true);
      if (dragTimerRef.current) window.clearTimeout(dragTimerRef.current);
    }
  };

  useEffect(() => {
    window.addEventListener('dragover', dragOverEventListener);
    return () => {
      window.removeEventListener('dragover', dragOverEventListener);
    };
  }, []);

  const [showAddTag, setShowAddTag] = React.useState<boolean>(false);
  const [selectedTagId, setSelectedTagId] = React.useState<string>('');

  return (
    <div className={styles[`footer-status-em_atendimento`]}>
      <Modal
        show={showAddTag}
        onClose={() => setShowAddTag(false)}
        onCancel={() => setShowAddTag(false)}
        onConfirm={() => {
          onChatAddTag(chatId, +selectedTagId);
          setShowAddTag(false);
        }}
        title="Adicionar marcador ao chat"
        confirmProps={{
          label: 'Adicionar',
        }}
      >
        <VBox>
          <CampoSeletor
            options={optionsTags?.filter(
              (it: any) => !chatTags?.includes(it.nome),
            )}
            value={selectedTagId?.toString()}
            onChange={(value: string | null) => setSelectedTagId(value!)}
            placeholder="Selecione um marcador"
          />
          <AlertBox variant="info">
            O marcador será adicionado permanentemente ao chat e ficará visível
            aos outros atendentes.
          </AlertBox>
        </VBox>
      </Modal>

      <Modal
        hideControls
        show={!recording && showAtalhos}
        title="Atalhos de Mensagens"
        onClose={() => setShowAtalhos(false)}
        onCancel={() => setShowAtalhos(false)}
      >
        <AtalhosBox
          atalhosPessoais={atalhosPessoais}
          atalhosGlobais={atalhosGlobais}
          onSelectAtalho={m => {
            atualizarMensagem(mensagem + m);
            campoMensagemRef.current?.focus();
            setShowAtalhos(false);
          }}
        />
      </Modal>

      <Modal
        hideControls
        show={showAttachments}
        title="Enviar anexos"
        onClose={cancelarAnexos}
        // Com arquivo escolhido, clique no fundo não descarta sem querer.
        ignoreOverlayClick={attachments.length > 0}
        size="md"
      >
        <VBox gap="12px">
          <CampoUpload
            instanceId="upload-anexo"
            onChange={setAttachments}
            value={attachments}
            accept="*/*"
            maxSize={MAX_FILE_SIZE}
            hideFileList
            disabled={isSending}
          />

          {attachments.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {attachments.map((it, idx) => (
                <ArquivoAnexo
                  key={`${it.name}-${it.size}-${idx}`}
                  onRemove={() =>
                    setAttachments(attachments.filter((_, i) => i !== idx))
                  }
                  arquivo={it}
                />
              ))}
            </div>
          )}

          <label>Legenda (opcional)</label>
          <textarea
            className="rr-block"
            style={{
              border: '1px solid var(--tc-color-gray-300)',
              borderRadius: '8px',
              padding: '4px',
              outline: 'none',
              width: '100%',
              resize: 'vertical',
              minHeight: '4rem',
            }}
            placeholder="Enter envia, Shift+Enter para nova linha."
            value={legenda}
            disabled={isSending}
            onChange={e => setLegenda(e.target.value)}
            onKeyDown={e => {
              // Esc segue seu caminho: é como o modal fecha.
              if (e.key === 'Escape') return;
              e.stopPropagation();
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                enviarAnexos();
              }
            }}
          />

          <HBox gap="8px" style={{ justifyContent: 'flex-end' }}>
            <Botao variant="outline-secondary" onClick={cancelarAnexos} disabled={isSending}>
              Cancelar
            </Botao>
            <Botao
              data-testid="btn-enviar-anexos"
              variant="primary"
              icon={mdiSendVariant}
              disabled={isSending || !attachments.length}
              onClick={enviarAnexos}
            >
              Enviar
            </Botao>
          </HBox>
        </VBox>
      </Modal>

      <form
        onSubmit={e => {
          e.preventDefault();
          // Anexo tem caminho próprio, pelo modal: daqui só sai texto.
          if (!mensagem.length || isSending) return;
          onSend(mensagem, []);
        }}
      >
        <HBox gap="8px">
          {recording ? (
            <>
              <div style={{ flex: 1 }}></div>
              <Botao
                variant="none-secondary"
                icon={mdiTrashCan}
                onClick={cancelRecording}
              />
              <div>{recordProgress}</div>
              <Icon
                path={mdiCircle}
                color="var(--tc-color-danger)"
                size={0.75}
                className={styles.blink}
              />
              <div ref={waveformRef} className={styles.waveform}></div>
              <Botao
                variant="none-primary"
                icon={mdiSendVariant}
                disabled={isSending}
                onClick={sendAudio}
              ></Botao>
            </>
          ) : (
            <>
              <Botao
                noArrow
                variant="none-primary"
                icon={mdiTagPlus}
                disabled={isSending}
                position="top-left"
                onClick={() => setShowAddTag(true)}
              />
              <Botao
                noArrow
                variant="none-primary"
                icon={mdiMessageBookmark}
                disabled={isSending}
                position="top-left"
                onClick={() => setShowAtalhos(true)}
              />
              <textarea
                id="mensagem"
                ref={campoMensagemRef}
                className='rr-block'
                style={{
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  width: '100%',
                }}
                placeholder="Digite sua mensagem. Shift+Enter para nova linha. Ctrl+1-9 para atalhos."
                value={mensagem}
                onChange={e => {
                  atualizarMensagem(e.target.value);

                  if (triggerIsTyping()) {
                    onTyping?.(true);
                  }

                  if (timeoutIsTyping.current) {
                    clearTimeout(timeoutIsTyping.current);
                  }
                  timeoutIsTyping.current = setTimeout(() => {
                    onTyping?.(false);
                    resetTriggerIsTyping();
                  }, TYPING_COOLDOWN);

                }}
                disabled={isSending}
                rows={mensagem.split('\n').length || 1}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      (e.target as HTMLInputElement).form?.dispatchEvent(
                        new Event('submit', {
                          bubbles: true,
                          cancelable: true,
                        }),
                      );
                    }
                  } else {
                    if (e.ctrlKey && e.key >= '1' && e.key <= '9') {
                      e.preventDefault();
                      e.stopPropagation();
                      const idx = parseInt(e.key) - 1;
                      const atalho = atalhosPessoais?.[idx];
                      if (atalho) {
                        atualizarMensagem(mensagem + atalho.conteudo);
                      }
                    } else if (e.altKey && e.key >= '1' && e.key <= '9') {
                      e.preventDefault();
                      e.stopPropagation();
                      const idx = parseInt(e.key) - 1;
                      const atalho = atalhosGlobais?.[idx];
                      if (atalho) {
                        atualizarMensagem(mensagem + atalho.conteudo);
                      }
                    } else {
                      e.stopPropagation();
                    }
                  }
                }}
                onPaste={handlePaste}
              />
              {!mensagem.length && (
                <Botao
                  variant="none-primary"
                  icon={mdiMicrophone}
                  disabled={isSending}
                  onClick={recAudio}
                ></Botao>
              )}
              <Botao
                variant="none-primary"
                icon={mdiAttachment}
                disabled={isSending}
                onClick={() => setShowAttachments(true)}
              ></Botao>
              {mensagem.length > 0 && (
                <Botao
                  data-testid="btn-enviar"
                  type="submit"
                  variant="none-primary"
                  icon={mdiSendVariant}
                  disabled={isSending}
                ></Botao>
              )}
            </>
          )}
        </HBox>
      </form>
    </div>
  );
};
